import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { IconArchive, IconInbox, IconTrash } from "@tabler/icons-react"
import { usePostHog } from "posthog-js/react"

interface SenderActionModalProps {
  isOpen: boolean
  onClose: () => void
  sender: string
  onAction: (action: 'delete' | 'archive' | 'do_nothing') => void
  totalSenders: number
  currentSender: number
}

export function SenderActionModal({
  isOpen,
  onClose,
  sender,
  onAction,
  totalSenders,
  currentSender
}: SenderActionModalProps) {
  const posthog = usePostHog()

  const handleAction = (action: 'delete' | 'archive' | 'do_nothing') => {
    posthog.capture('sender_action_taken', { action, sender, total_senders: totalSenders })
    onAction(action)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <div className="flex items-center justify-between mb-6">
          {totalSenders > 1 && (
            <div className="bg-primary/10 px-3 py-1 rounded-full text-xs font-medium">
              {currentSender} of {totalSenders}
            </div>
          )}
        </div>
        <DialogHeader>
          <DialogTitle className="text-xl leading-relaxed">Future Email Preferences</DialogTitle>
          <div className="space-y-3 pt-3">
            <div className="bg-muted/50 p-3 rounded-lg">
              <div className="text-sm text-muted-foreground">Newsletter from:</div>
              <div className="font-medium text-lg text-foreground">{sender}</div>
            </div>
            <div className="text-sm">
              Choose how to handle future emails from this sender as we continue analyzing your inbox:
            </div>
          </div>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4 py-6">
          <Button
            variant="outline"
            className="flex flex-col items-center gap-3 h-auto py-6 hover:border-destructive/50 hover:bg-destructive/5 group"
            onClick={() => handleAction('delete')}
          >
            <div className="p-2 rounded-full bg-destructive/10 text-destructive group-hover:bg-destructive/20">
              <IconTrash className="h-5 w-5" />
            </div>
            <div className="space-y-1 text-center">
              <p className="font-medium">Delete</p>
              <p className="text-xs text-muted-foreground leading-tight">
                Remove all<br />permanently
              </p>
            </div>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center gap-3 h-auto py-6 hover:border-blue-500/50 hover:bg-blue-500/5 group"
            onClick={() => handleAction('archive')}
          >
            <div className="p-2 rounded-full bg-blue-500/10 text-blue-500 group-hover:bg-blue-500/20">
              <IconArchive className="h-5 w-5" />
            </div>
            <div className="space-y-1 text-center">
              <p className="font-medium">Archive</p>
              <p className="text-xs text-muted-foreground leading-tight">
                Move to<br />archive
              </p>
            </div>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center gap-3 h-auto py-6 hover:border-green-500/50 hover:bg-green-500/5 group"
            onClick={() => handleAction('do_nothing')}
          >
            <div className="p-2 rounded-full bg-green-500/10 text-green-500 group-hover:bg-green-500/20">
              <IconInbox className="h-5 w-5" />
            </div>
            <div className="space-y-1 text-center">
              <p className="font-medium">Inbox</p>
              <p className="text-xs text-muted-foreground leading-tight">
                Leave in<br />inbox
              </p>
            </div>
          </Button>
        </div>
        <DialogFooter className="sm:justify-start border-t pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            Ask me again later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 
